import { ChevronDown, ChevronUp, CloudUpload, FileSpreadsheet, Trash2 } from 'lucide-react'
import { cloneElement, isValidElement, useCallback, useState, useEffect } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useDropzone } from 'react-dropzone'
import type { Accept, FileWithPath } from 'react-dropzone'
import classNames from 'classnames'

import Button from '@/elements/Button'
import ExternalLinkCard from '@/elements/ExternalLinkCard'
import Flex from '@/elements/Flex'
import Modal from '@/components/Modal'
import Text from '@/elements/Text'
import Loading from '@/elements/Loading'
import ImageGrid from '@/components/ImageGrid'
import { formatFileSize } from '@/utils/format'
import { colors } from '@/utils/theme'
import useUploadErrors, { type UploadErrorsType } from '@/hooks/useUploadErrors'
import { MakeACopyImage, SaveAsCSVImage } from '@/assets'

import './UploadModal.scss'

interface UploadModalProps {
  title: string
  children?: ReactNode
  description?: string
  isMulti?: boolean
  accept?: Accept
  onUpload: (files: File[]) => Promise<UploadErrorsType | null>
  sampleFileUrl: string
}

function UploadModal({
  title,
  children,
  description = 'Select file',
  isMulti,
  accept,
  onUpload,
  sampleFileUrl,
}: UploadModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [internalFiles, setInternalFiles] = useState<FileWithPath[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadErrors, setUploadErrors] = useState<UploadErrorsType>()
  const [showInstructions, setShowInstructions] = useState(false)

  const { errorItems, errorMessage } = useUploadErrors(uploadErrors)

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept,
    multiple: isMulti,
    disabled: isUploading
  })

  const renderTrigger = () => {
    if (children && isValidElement(children)) {
      const element = children as ReactElement<{ onClick?: (e: React.MouseEvent) => void }>
      return cloneElement(element, {
        onClick: (e: React.MouseEvent) => {
          e?.preventDefault?.()
          setIsOpen(true)
          element.props?.onClick?.(e)
        }
      })
    }
    return null
  }

  const handleRemoveFile = useCallback(
    (fileToRemove: FileWithPath) => () => {
      if (isUploading) return
      setInternalFiles(prev => prev.filter(f => f.path !== fileToRemove.path))
      setUploadErrors(undefined)
    },
    [isUploading]
  )

  const handleUpload = useCallback(async () => {
    if (internalFiles.length === 0) return

    setIsUploading(true)
    setUploadErrors(undefined)

    const errors = await onUpload(internalFiles)
    setIsUploading(false)
    if (errors) {
      setUploadErrors(errors)
      return
    }
    setInternalFiles([])
    setIsOpen(false)
  }, [internalFiles, onUpload])

  useEffect(() => {
    if (acceptedFiles.length === 0) return

    setInternalFiles(prev => {
      if (isMulti) {
        const existing = new Set(prev.map(f => f.path))
        const newOnes = acceptedFiles.filter(f => !existing.has(f.path))
        return [...prev, ...newOnes]
      }
      return [acceptedFiles[0]]
    })
    setUploadErrors(undefined)
  }, [acceptedFiles, isMulti])

  return (
    <>
      {renderTrigger()}
      <Modal
        isOpen={isOpen}
        className='upload-modal'
        title={title}
        onCancel={() => {
          if (isUploading) return
          setIsOpen(false)
          setInternalFiles([])
          setUploadErrors(undefined)
        }}
        onSave={handleUpload}
        saveLabel={isUploading ? 'Uploading…' : 'Upload'}
        disabled={internalFiles.length === 0 || isUploading}
        preventClickOutsideClose
        style={{ width: 642 }}
      >
        {errorItems.length > 0 && (
          <Flex
            wfull
            p={3}
            mb={3}
            gap={1}
            className='error-card'
          >
            <Text color='red' variant='h4'>{errorMessage}</Text>
            <Flex mt={2} gap={2}>
              {errorItems.map(({ row, errors }) => (
                <Flex key={row} component='ul'>
                  <li key={row}>
                    <Text variant='callout'>
                      {`Row ${row}: `}
                    </Text>
                  </li>
                  <Flex component='ul'>
                    {errors.map((error, index) => (
                      <li key={index}>
                        <Text variant='textblock'>
                          {error}
                        </Text>
                      </li>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}

        {isUploading ? (
          <Flex wfull align='center' py={8} justify='center'>
            <Loading />
          </Flex>
        ) : (
          <Flex wfull align='flex-end' gap={3}>
            <div
              {...getRootProps({
                className: classNames('dropzone', {
                  'dropzone-focused': isFocused,
                  'dropzone-accepted': isDragAccept,
                  'dropzone-rejected': isDragReject,
                  'dropzone-disabled': isUploading
                }),
              })}
            >
              <input {...getInputProps()} />
              <Flex align='center' justify='center' wfull gap={3}>
                <CloudUpload size={48} color={colors['black-light-60']} />
                <Flex align='center' justify='center' gap={1}>
                  <Text color='black-light-20'>{description}</Text>
                  <Text color='black-light-40' variant='textblock'>
                    or drag and drop it here
                  </Text>
                </Flex>
              </Flex>
            </div>

            {sampleFileUrl && internalFiles.length === 0 && (
              <Flex wfull gap={2}>
                <ExternalLinkCard
                  to={sampleFileUrl || ''}
                  targetBlank
                >
                  <Flex gap={1}>
                    <Flex inline align='center' gap={1}>
                      <FileSpreadsheet size={18} color={colors['primary']} />
                      <Text color='black-light-20'>
                        Get the template file here
                      </Text>
                    </Flex>
                  </Flex>
                </ExternalLinkCard>
                <Flex wfull gap={1}>
                  <Flex
                    wfull
                    inline
                    justify='space-between'
                    onClick={() => setShowInstructions(!showInstructions)}
                    className={classNames('pressable', {
                      'pressable-active': showInstructions,
                    })}
                  >
                    <Text variant='h4' color='black-light-20'>
                      How to create your own CSV file to upload?
                    </Text>
                    {showInstructions ? (
                      <ChevronUp size={24} color={colors['black-light-20']} />
                    ) : (
                      <ChevronDown size={24} color={colors['black-light-20']} />
                    )}
                  </Flex>
                  {showInstructions && (
                    <Flex
                      p={3}
                      gap={3}
                      radius='medium'
                      color='gray-light-60'
                    >
                      <Flex gap={1}>
                        <Text variant='textblock' color='black-light-20'>
                          <b>1.</b> Open the template and create a copy.
                        </Text>
                        <ImageGrid images={[MakeACopyImage]} imageGap={5} />
                      </Flex>
                      <Text variant='textblock' color='black-light-20'>
                        <b>2.</b> Insert your data into the relevant columns.
                      </Text>
                      <Flex gap={1}>
                        <Text variant='textblock' color='black-light-20'>
                          <b>3.</b> Download the file as CSV format and upload it here.
                        </Text>
                        <ImageGrid images={[SaveAsCSVImage]} />
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            )}

            {internalFiles.length > 0 && (
              <Flex wfull component='ul' gap={2}>
                {internalFiles.map(file => (
                  <Flex
                    key={file.path}
                    wfull
                    inline
                    component='li'
                    justify='space-between'
                    align='center'
                    color='gray-light-80'
                    style={{ border: `1px solid ${colors['gray-light-20']}` }}
                    radius='small'
                    gap={2}
                    p={3}
                  >
                    <Flex inline gap={2} align='center'>
                      <FileSpreadsheet size={18} style={{ minWidth: 18 }} color={colors['primary']} />
                      <Flex inline align='center' gap={1}>
                        <Text color='black-light-20' maxLines={1}>{file.name}</Text>
                        <Text variant='textblock' color='black-light-40'>•</Text>
                        <Text variant='textblock' color='black-light-40' style={{ textWrap: 'nowrap' }}>
                          {formatFileSize(file.size)}
                        </Text>
                      </Flex>
                    </Flex>
                    <Button
                      variant='iconDangerBtn'
                      small
                      disabled={isUploading}
                      onClick={handleRemoveFile(file)}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        )}
      </Modal>
    </>
  )
}

export default UploadModal
